import { z } from 'zod';
import { I18nText } from '../i18n';
import { CategoryAwardConfigSchema } from '../config/scoring';

/** GET /api/v1/links/:token — resolve any share token (SPA bootstrap). */
export const LinkResolveResponseSchema = z.object({
  purpose: z.enum(['PUBLIC_VIEW', 'CADDY_PORTAL', 'ADMIN_CONSOLE', 'REGISTRATION']),
  tournamentId: z.uuid(),
  tournamentName: z.string(),
  status: z.string(),
  /** CADDY_PORTAL only: false = the caddy logs in with code + bib, no PIN (additive, §15 #472) */
  caddyPin: z.boolean().optional(),
});
export type LinkResolveResponse = z.infer<typeof LinkResolveResponseSchema>;

/** GET /api/v1/public/t/:token — tournament meta for spectators/TV/sign-up. */
export const PublicTournamentSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  description: z.string().nullable(),
  startDate: z.string(),
  endDate: z.string(),
  timezone: z.string(),
  status: z.string(),
  formatKind: z.string(),
  views: z.array(z.string()),
  holesPerRound: z.number(),
  venue: z.object({ tenantName: z.string(), courseNames: z.array(z.string()) }),
  rounds: z.array(
    z.object({
      id: z.uuid(),
      roundNo: z.number(),
      date: z.string(),
      status: z.string(),
      /** layouts played in the round (first = Round.courseId); groups/rows carry courseId */
      courses: z.array(
        z.object({
          id: z.uuid(),
          name: z.string(),
          holes: z.array(z.object({ number: z.number(), par: z.number() })),
        }),
      ),
    }),
  ),
  categories: z.array(
    z.object({
      key: z.string(),
      label: I18nText,
      showInFilter: z.boolean(),
      awardRanking: z.boolean(),
      options: z.array(z.object({ value: z.string(), label: I18nText })),
      /** effective 분류별 시상 settings (ScoringConfig.categoryAwards + this category's override) */
      awardConfig: CategoryAwardConfigSchema.optional(),
    }),
  ),
  contests: z.array(
    z.object({
      id: z.uuid(),
      type: z.enum(['NEAREST_PIN', 'LONGEST_DRIVE']),
      holeNumber: z.number(),
      /** null = held in every round (one leader per round) */
      roundNo: z.number().nullable(),
      courseId: z.uuid().nullable(),
      name: z.string().nullable(),
    }),
  ),
  branding: z.object({
    logoUrl: z.string().nullable(),
    primaryColor: z.string(),
    secondaryColor: z.string(),
    accentColor: z.string(),
    headline: I18nText.nullable(),
    footerText: I18nText.nullable(),
    tvScrollSeconds: z.number(),
  }),
  sponsors: z.array(
    z.object({
      id: z.uuid(),
      name: z.string(),
      imageUrl: z.string(),
      linkUrl: z.string().nullable(),
      placement: z.string(),
    }),
  ),
  hasMatches: z.boolean(),
  realtimeVersion: z.number(),
  /** policies.hideLastHoles: the last N holes of each round are hidden from the public until the
   * round is locked (§6) — lets spectator pages say so. Optional (older servers omit it = 0). */
  hideLastHoles: z.int().min(0).optional(),
  /** the award sheet is published (GET /public/t/:token/award-sheet, §15 #478) — additive */
  awardsPublished: z.boolean().optional(),
});
export type PublicTournament = z.infer<typeof PublicTournamentSchema>;
